import { DEFAULT_COUPON_CODE } from '@lyka/constants'
import { atom } from 'nanostores'
import { useCookies } from './useCookies'
import { useExperiments } from './useExperiments'
import { type Coupon, getCoupon } from '@/services/coupon'

const state = (): Partial<{
  loaded: boolean
  coupon?: Coupon
}> => ({
  loaded: false,
  coupon: undefined,
})

const $state = atom(state())

const DEPRECATED_COUPONS: ReadonlySet<string> = new Set<string>([
  'FRESH20',
  'FRESH30',
  'HEALTHY30',
  'WELCOME2020',
  'BLACKFRIDAY',
])

const saveCouponToCookie = (coupon: Coupon): void => {
  const ONE_YEAR_SECONDS = 365 * 24 * 60 * 60
  const COUPON_COOKIE_NAME = 'coupon' as const
  const REFERRER_COOKIE_NAME = 'referrer' as const

  useCookies().set(COUPON_COOKIE_NAME, coupon.code, ONE_YEAR_SECONDS)

  if (coupon.referrer) {
    useCookies().set(REFERRER_COOKIE_NAME, coupon.code, ONE_YEAR_SECONDS)
  }
}

const DEFAULT_COUPON_EXPERIMENT = 'experiment-coupon' as const
const COUPON_COOKIE_NAME = 'coupon' as const

// Set to true to allow loading the current applied coupon from cookie
const SHOW_APPLIED_COUPON = false as const

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useCoupons = () => {
  const getCouponFromFlag = async (): Promise<Coupon | undefined> => {
    const code = await useExperiments().getVariant(DEFAULT_COUPON_EXPERIMENT)

    if (code && typeof code === 'string') {
      const coupon = await getCoupon(code)

      return coupon
    }
  }

  const getCouponFromCookie = async (): Promise<Coupon | undefined> => {
    const code = useCookies().get(COUPON_COOKIE_NAME)

    if (code) {
      const isDeprecated = DEPRECATED_COUPONS.has(code)

      // If coupon saved in cookie is deprecated then strip it out
      if (isDeprecated) {
        useCookies().remove(COUPON_COOKIE_NAME)

        return undefined
      }

      const coupon = await getCoupon(code)

      return coupon
    }

    return undefined
  }

  const getDefaultCoupon = async (): Promise<Coupon | undefined> => {
    const code = DEFAULT_COUPON_CODE

    const coupon = await getCoupon(code)

    return coupon
  }

  const getCouponFromURL = async (): Promise<Coupon | undefined> => {
    const COUPON_PARAM = 'cp'

    const params = new URLSearchParams(window.location.search)
    const code = params.get(COUPON_PARAM)

    if (code) {
      const coupon = await getCoupon(code)

      if (coupon) {
        saveCouponToCookie(coupon)
      }

      return coupon
    }

    return undefined
  }

  // This generator will yield coupons in the following order:
  // 1. From URL
  // 2. From cookie
  // 3. From flag
  // 4. From default constant
  // 5. undefined
  const couponGenerator = async function* (): AsyncGenerator<Coupon | undefined, undefined, unknown> {
    yield await getCouponFromURL()

    if (SHOW_APPLIED_COUPON) {
      yield await getCouponFromCookie()
    }

    yield await getCouponFromFlag()
    yield await getDefaultCoupon()

    return undefined
  }

  const load = async (): Promise<void> => {
    let coupon: Coupon | undefined

    const generator = couponGenerator()

    // Iterate over coupon generator and get the first coupon that is not undefined
    for await (const value of generator) {
      if (value) {
        coupon = value
        break
      }
    }

    if (coupon) {
      $state.set({
        loaded: true,
        coupon,
      })
    } else {
      $state.set({
        loaded: true,
      })
    }
  }

  const ready = (): Promise<Coupon | undefined> => {
    return new Promise((resolve) => {
      $state.subscribe(({ coupon, loaded }) => {
        if (loaded) {
          resolve(coupon)
        }
      })
    })
  }

  return {
    load,
    ready,
  }
}
